import { INFOPANE_ARROW_PADDING, INFOPANE_ARROW_SIZE } from './markerConstants';
import { InfoPanePosition } from './TTMarker.types';

/**
 * Arrow positioning utilities for TTMarker component.
 * Pure functions for calculating arrow placement on InfoPane.
 */

/**
 * Clamps a value within specified bounds.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculates horizontal position for arrow (used when InfoPane is above/below marker).
 *
 * @param panelWidth - InfoPane width in pixels
 * @returns Horizontal position for arrow wrapper (left style value)
 */
export function calculateArrowHorizontalPosition(panelWidth: number): number {
  return clamp(
    panelWidth / 2 - INFOPANE_ARROW_SIZE / 2,
    INFOPANE_ARROW_PADDING,
    panelWidth - INFOPANE_ARROW_SIZE - INFOPANE_ARROW_PADDING,
  );
}

/**
 * Calculates vertical position for arrow (used when InfoPane is left/right of marker).
 *
 * @param panelHeight - InfoPane height in pixels
 * @returns Vertical position for arrow wrapper (top style value)
 */
export function calculateArrowVerticalPosition(panelHeight: number): number {
  return clamp(
    panelHeight / 2 - INFOPANE_ARROW_SIZE / 2,
    INFOPANE_ARROW_PADDING,
    panelHeight - INFOPANE_ARROW_SIZE - INFOPANE_ARROW_PADDING,
  );
}

/**
 * Determines whether arrow should be positioned horizontally or vertically
 * based on InfoPane position.
 *
 * @param position - InfoPane position relative to marker
 * @returns 'horizontal' if arrow moves left/right, 'vertical' if up/down
 */
export function getArrowPositionAxis(position: InfoPanePosition): 'horizontal' | 'vertical' {
  return position === 'above' || position === 'below' ? 'horizontal' : 'vertical';
}

/**
 * Gets the CSS class name for arrow wrapper based on position.
 *
 * @param position - InfoPane position relative to marker
 * @param styles - CSS modules styles object
 * @returns CSS class name for arrow rotation/positioning
 */
export function getArrowWrapperClass(
  position: InfoPanePosition,
  styles: Record<string, string>,
): string {
  switch (position) {
    case 'below':
      return styles.arrowWrapperTop;
    case 'left':
      return styles.arrowWrapperRight;
    case 'right':
      return styles.arrowWrapperLeft;
    default: // 'above'
      return styles.arrowWrapperBottom;
  }
}

/**
 * Calculates arrow position and returns style updates.
 *
 * @param position - InfoPane position relative to marker
 * @param panelWidth - InfoPane width
 * @param panelHeight - InfoPane height
 * @returns Object with style properties to apply to arrow wrapper
 */
export function calculateArrowStyles(
  position: InfoPanePosition,
  panelWidth: number,
  panelHeight: number,
): {
  left?: string;
  top?: string;
  width: string;
  height: string;
} {
  const axis = getArrowPositionAxis(position);

  if (axis === 'horizontal') {
    const leftPosition = calculateArrowHorizontalPosition(panelWidth);
    return {
      left: `${Math.round(leftPosition)}px`,
      width: `${INFOPANE_ARROW_SIZE}px`,
      height: `${INFOPANE_ARROW_SIZE}px`,
    };
  }

  const topPosition = calculateArrowVerticalPosition(panelHeight);
  return {
    top: `${Math.round(topPosition)}px`,
    width: `${INFOPANE_ARROW_SIZE}px`,
    height: `${INFOPANE_ARROW_SIZE}px`,
  };
}
